import { Check, DollarSign } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/_components';

import type { InvoiceLifecycleStatus } from '../schemas/status-options';
import { invoiceLifecycleStatusOptions } from '../schemas/status-options';

type InvoiceStatusCellProps = {
  status: InvoiceLifecycleStatus | null | undefined;
};

const cycleStyles: Record<InvoiceLifecycleStatus, { wrap: string; icon: string; label: string }> = {
  open: {
    wrap: 'bg-gray-200',
    icon: 'text-green-800',
    label: 'Open',
  },
  paid: {
    wrap: 'bg-green-100',
    icon: 'text-green-800',
    label: 'Paid',
  },
  'partially paid': {
    wrap: 'bg-warning-x-light',
    icon: 'text-warning',
    label: 'Partially Paid',
  },
  'past due': {
    wrap: 'bg-orange-100',
    icon: 'text-orange-600',
    label: 'Past Due',
  },
  uncollectable: {
    wrap: 'bg-red-100',
    icon: 'text-red-800',
    label: 'Uncollectable',
  },
  void: {
    wrap: 'bg-gray-200',
    icon: 'text-gray-800',
    label: 'Void',
  },
  'write-off': {
    wrap: 'bg-red-100',
    icon: 'text-red-800',
    label: 'Write-Off',
  },
};

function StatusGlyph({ status }: { status: InvoiceLifecycleStatus }) {
  const s = cycleStyles[status];

  const common = twMerge('flex size-7 items-center justify-center rounded-full text-xs font-semibold', s?.wrap);

  switch (status) {
    case 'open':
      return (
        <span className={common}>
          <DollarSign className={twMerge('size-4', s.icon)} strokeWidth={2.5} aria-hidden />
        </span>
      );
    case 'paid':
      return (
        <span className={common}>
          <Check className={twMerge('size-4', s.icon)} strokeWidth={2.5} aria-hidden />
        </span>
      );
    case 'partially paid':
      return (
        <span className={common}>
          <span className={s.icon}>P</span>
        </span>
      );
    case 'past due':
      return (
        <span className={common}>
          <DollarSign className={twMerge('size-4', s.icon)} strokeWidth={2.5} aria-hidden />
        </span>
      );
    case 'uncollectable':
      return (
        <span className={common}>
          <span className={s.icon}>U</span>
        </span>
      );
    case 'void':
      return (
        <span className={common}>
          <span className={s.icon}>V</span>
        </span>
      );
    case 'write-off':
      return (
        <span className={common}>
          <span className={s.icon}>W</span>
        </span>
      );
    default:
      return null;
  }
}

export function InvoiceStatusCell({ status }: InvoiceStatusCellProps) {
  if (status == null) {
    return <span className="text-c-card-muted">—</span>;
  }

  const label = invoiceLifecycleStatusOptions.find((o) => o.value === status)?.name ?? cycleStyles?.[status]?.label;

  return (
    <Tooltip content={label} placement="top" zIndex={50}>
      <span className="inline-flex cursor-default">
        <StatusGlyph status={status} />
      </span>
    </Tooltip>
  );
}
